import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PlusIcon, BeakerIcon, EyeIcon, XMarkIcon, TrashIcon, LinkIcon, CalendarIcon, ClockIcon, ChartBarIcon, MicrophoneIcon, SparklesIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { authService } from '../../services/authService';
import campaignService, { Campaign } from '../../services/campaignService';
import CampaignScheduler from './CampaignScheduler';
import CampaignHistory from './CampaignHistory';
import ScheduleModal from './ScheduleModal';
import { VoiceSettingsManager } from '../VoiceSettings';
import CampaignAIEditor from './CampaignAIEditor';
import CampaignPreview from './CampaignPreview';


interface CampaignDashboardProps {
  onCreateCampaign: () => void;
  onEditCampaign: (campaign: Campaign) => void;
  onGenerateContent: () => void;
  refreshTrigger?: number;
}

const CampaignDashboard: React.FC<CampaignDashboardProps> = ({
  onCreateCampaign,
  onEditCampaign,
  onGenerateContent,
  refreshTrigger
}) => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterApprovalStatus, setFilterApprovalStatus] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'campaigns' | 'scheduler' | 'history' | 'voice-settings'>('campaigns');
  const [scheduleModalCampaign, setScheduleModalCampaign] = useState<Campaign | null>(null);
  const [aiEditorCampaign, setAiEditorCampaign] = useState<Campaign | null>(null);
  const [previewCampaign, setPreviewCampaign] = useState<Campaign | null>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    [key: number]: {
      direction: 'bottom' | 'top';
      coordinates: {
        top?: number;
        bottom?: number;
        right: number;
      };
    }
  }>({});
  const dropdownRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Pixel-perfect dropdown positioning with exact coordinates
  const calculateDropdownPosition = (buttonElement: HTMLButtonElement) => {
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    // Conservative estimate: dropdown needs ~200px height
    const DROPDOWN_HEIGHT = 200;
    const GAP = 8; // 8px gap from button

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    console.log('🔍 Dropdown Position Debug:', {
      buttonRect: rect,
      viewportHeight,
      viewportWidth,
      spaceBelow,
      spaceAbove,
      DROPDOWN_HEIGHT
    });

    let direction: 'bottom' | 'top';
    let coordinates: {
      top?: number;
      bottom?: number;
      right: number;
    };

    // Calculate right position (distance from right edge of viewport)
    const rightPosition = viewportWidth - rect.right;

    if (spaceBelow < DROPDOWN_HEIGHT && spaceAbove >= DROPDOWN_HEIGHT) {
      // Position above button
      direction = 'top';
      coordinates = {
        bottom: viewportHeight - rect.top + GAP,
        right: rightPosition
      };
      console.log('✅ Positioning ABOVE button:', coordinates);
    } else {
      // Position below button (default)
      direction = 'bottom';
      coordinates = {
        top: rect.bottom + GAP,
        right: rightPosition
      };
      console.log('✅ Positioning BELOW button:', coordinates);
    }

    return { direction, coordinates };
  };

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterApprovalStatus !== 'all') {
        params.append('approval_status', filterApprovalStatus);
      }

      const response = await fetch(`${API_BASE_URL}/api/campaigns?${params}`, {
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to load campaigns');
      }

      const data = await response.json();
      setCampaigns(data);
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError(err instanceof Error ? err.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL, filterStatus, filterApprovalStatus]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns, refreshTrigger]);

  // Handle viewport changes for open dropdowns - reposition instead of closing
  useEffect(() => {
    const handleViewportChange = () => {
      if (openDropdown !== null) {
        // Find the button for the open dropdown and recalculate position
        const buttonElement = document.querySelector(`button[data-dropdown-id="${openDropdown}"]`) as HTMLButtonElement;

        if (buttonElement) {
          const newPositionData = calculateDropdownPosition(buttonElement);
          setDropdownPosition(prev => ({ ...prev, [openDropdown]: newPositionData }));
        } else {
          // If button not found, close dropdown as fallback
          setOpenDropdown(null);
        }
      }
    };

    window.addEventListener('scroll', handleViewportChange, { passive: true });
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [openDropdown]);

  const getStatusBadge = (approval_status: string, publish_status: string) => {
    const getStatusInfo = () => {
      if (publish_status === 'sent') {
        return { color: 'bg-green-100 text-green-800', text: 'Sent' };
      }
      if (publish_status === 'scheduled') {
        return { color: 'bg-purple-100 text-purple-800', text: 'Scheduled' };
      }
      if (publish_status === 'published') {
        return { color: 'bg-blue-100 text-blue-800', text: 'Published' };
      }
      if (approval_status === 'approved') {
        return { color: 'bg-green-100 text-green-800', text: 'Approved' };
      }
      if (approval_status === 'pending') {
        return { color: 'bg-yellow-100 text-yellow-800', text: 'Pending Approval' };
      }
      if (approval_status === 'rejected') {
        return { color: 'bg-red-100 text-red-800', text: 'Rejected' };
      }
      return { color: 'bg-gray-100 text-gray-800', text: 'Draft' };
    };

    const { color, text } = getStatusInfo();

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>
        {text}
      </span>
    );
  };

  const getDataQualityInfo = (campaign: Campaign) => {
    // Extract data validation info from generation metadata
    const metadata = campaign.generation_metadata;
    const dataValidation = metadata?.data_validation;
    const personalizationLevel = metadata?.personalization_level || 'medium';

    // Get personalization level badge
    const getPersonalizationBadge = (level: string) => {
      const levelInfo = {
        'light': { color: 'bg-green-100 text-green-800', text: 'Light', icon: '📝' },
        'medium': { color: 'bg-blue-100 text-blue-800', text: 'Medium', icon: '🎯' },
        'heavy': { color: 'bg-purple-100 text-purple-800', text: 'Heavy', icon: '🚀' }
      };
      const info = levelInfo[level as keyof typeof levelInfo] || levelInfo['medium'];
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${info.color}`}>
          <span className="mr-1">{info.icon}</span>
          {info.text}
        </span>
      );
    };

    // Get quality score badge
    const getQualityScore = () => {
      if (!dataValidation) {
        return <span className="text-xs text-gray-500">No data</span>;
      }

      const score = dataValidation.quality_score || 0;
      const sendable = dataValidation.sendable_leads || 0;
      const total = dataValidation.quality_score ?
        (sendable + (dataValidation.excluded_leads || 0)) :
        (campaign.estimated_recipients || 0);

      let scoreColor = 'text-gray-600';
      let bgColor = 'bg-gray-100';

      if (score >= 80) {
        scoreColor = 'text-green-700';
        bgColor = 'bg-green-50';
      } else if (score >= 60) {
        scoreColor = 'text-yellow-700';
        bgColor = 'bg-yellow-50';
      } else if (score > 0) {
        scoreColor = 'text-red-700';
        bgColor = 'bg-red-50';
      }

      return (
        <div className={`text-xs ${bgColor} rounded px-2 py-1`}>
          <div className={`font-medium ${scoreColor}`}>
            {score > 0 ? `${score}% quality` : 'No score'}
          </div>
          {sendable > 0 && total > 0 && (
            <div className="text-gray-600">
              {sendable}/{total} sendable
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-1">
        {getPersonalizationBadge(personalizationLevel)}
        {getQualityScore()}
      </div>
    );
  };

  const handleCreateApprovalLink = async (campaignId: number, versionNumber: number | null = null) => {
    try {
      const requestBody = versionNumber ? { version_number: versionNumber } : {};

      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/create-approval-link`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to create approval link');
      }

      const data = await response.json();

      // Verify we have a valid URL
      if (!data.preview_url) {
        throw new Error('No preview URL received from server');
      }

      console.log('Attempting to copy URL:', data.preview_url);

      const versionText = data.version_number ? ` (Version ${data.version_number})` : ' (Current Version)';

      // Copy to clipboard with fallback
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(data.preview_url);
          alert(`✅ Approval link copied to clipboard!${versionText}\n\n${data.preview_url}`);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = data.preview_url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();

          if (document.execCommand('copy')) {
            alert(`✅ Approval link copied to clipboard!${versionText}\n\n${data.preview_url}`);
          } else {
            throw new Error('Copy command failed');
          }

          document.body.removeChild(textArea);
        }
      } catch (clipboardError) {
        console.error('Clipboard copy failed:', clipboardError);
        // Show the URL to user for manual copying
        prompt('Copy this approval link:', data.preview_url);
      }

      // Refresh campaigns to show updated status
      loadCampaigns();
    } catch (err) {
      console.error('Error creating approval link:', err);
      alert('Failed to create approval link: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };


  const handlePublishToMailerLite = async (campaignId: number) => {
    if (!window.confirm('Are you sure you want to publish this campaign to MailerLite?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/publish`, {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to publish to MailerLite');
      }

      alert('Campaign published to MailerLite successfully!');
      loadCampaigns();
    } catch (err) {
      console.error('Error publishing to MailerLite:', err);
      alert('Failed to publish to MailerLite');
    }
  };

  const handleSendCampaign = async (campaignId: number) => {
    if (!window.confirm('Are you sure you want to send this campaign now? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/campaigns/${campaignId}/send`, {
        method: 'POST',
        headers: authService.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to send campaign');
      }

      alert('Campaign sent successfully!');
      loadCampaigns();
    } catch (err) {
      console.error('Error sending campaign:', err);
      alert('Failed to send campaign');
    }
  };

  const handleDeleteCampaign = async (campaignId: number, campaignName: string) => {
    if (!window.confirm(`Are you sure you want to delete the campaign "${campaignName}"?\n\nThis action cannot be undone and will also remove the campaign from MailerLite if it was published there.`)) {
      return;
    }

    try {
      const result = await campaignService.deleteCampaign(campaignId);
      
      if (result.success) {
        alert('✅ Campaign deleted successfully!');
        loadCampaigns(); // Refresh the list
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (err) {
      console.error('Error deleting campaign:', err);
      alert('❌ Failed to delete campaign: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleCancelSchedule = async (campaignId: number, campaignName: string) => {
    if (!window.confirm(`Are you sure you want to cancel the scheduled send for "${campaignName}"?\n\nThe campaign will remain published but will not be sent automatically.`)) {
      return;
    }

    try {
      const result = await campaignService.cancelScheduledCampaign(campaignId);
      
      if (result.success) {
        alert('✅ Campaign schedule canceled successfully!');
        loadCampaigns(); // Refresh the list
      } else {
        alert('❌ Failed to cancel schedule: ' + result.message);
      }
    } catch (err) {
      console.error('Error canceling campaign schedule:', err);
      alert('❌ Failed to cancel schedule: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const handleCopyExistingApprovalLink = async (approvalToken: string) => {
    try {
      // Use window.location.origin to always match current admin dashboard URL
      // Works in both development (localhost:3000) and production (Vercel)
      const previewUrl = `${window.location.origin}/preview/${approvalToken}`;

      console.log('Attempting to copy existing approval URL:', previewUrl);
      
      // Copy to clipboard with fallback
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(previewUrl);
          alert(`✅ Approval link copied to clipboard!\n\n${previewUrl}`);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = previewUrl;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          
          if (document.execCommand('copy')) {
            alert(`✅ Approval link copied to clipboard!\n\n${previewUrl}`);
          } else {
            throw new Error('Copy command failed');
          }
          
          document.body.removeChild(textArea);
        }
      } catch (clipboardError) {
        console.error('Clipboard copy failed:', clipboardError);
        // Show the URL to user for manual copying
        prompt('Copy this approval link:', previewUrl);
      }
    } catch (err) {
      console.error('Error copying approval link:', err);
      alert('Failed to copy approval link: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };


  const getPrimaryAction = (campaign: Campaign) => {
    // Determine the most important action for this campaign
    if (campaign.content_html) {
      return (
        <button
          onClick={() => setAiEditorCampaign(campaign)}
          className="inline-flex items-center px-3 py-1 text-sm font-medium text-purple-600 hover:text-purple-900"
        >
          <SparklesIcon className="h-4 w-4 mr-1" />
          Edit with AI
        </button>
      );
    }

    if (campaign.approval_status === 'draft' || campaign.approval_status === 'rejected') {
      return (
        <button
          onClick={() => onEditCampaign(campaign)}
          className="inline-flex items-center px-3 py-1 text-sm font-medium text-indigo-600 hover:text-indigo-900"
        >
          {campaign.approval_status === 'rejected' ? 'Edit & Revise' : 'Edit'}
        </button>
      );
    }

    if (campaign.approval_status === 'approved') {
      if (campaign.publish_status === 'published') {
        return (
          <button
            onClick={() => handleSendCampaign(campaign.id)}
            className="inline-flex items-center px-3 py-1 text-sm font-medium text-purple-600 hover:text-purple-900"
          >
            Send Now
          </button>
        );
      } else {
        return (
          <button
            onClick={() => handlePublishToMailerLite(campaign.id)}
            className="inline-flex items-center px-3 py-1 text-sm font-medium text-green-600 hover:text-green-900"
          >
            Publish to MailerLite
          </button>
        );
      }
    }

    return null;
  };

  const getSecondaryActions = (campaign: Campaign) => {
    const actions = [];

    // Preview (available for any campaign with content)
    if (campaign.content_html) {
      actions.push({
        key: 'preview',
        label: 'Preview',
        icon: EyeIcon,
        onClick: () => setPreviewCampaign(campaign),
        className: 'text-gray-700 hover:text-gray-900'
      });
    }


    // Send for approval
    if (campaign.approval_status === 'draft' || campaign.approval_status === 'rejected') {
      actions.push({
        key: 'approval',
        label: campaign.approval_status === 'rejected' ? 'Resubmit for Approval' : 'Send for Approval',
        icon: LinkIcon,
        onClick: () => handleCreateApprovalLink(campaign.id),
        className: 'text-blue-700 hover:text-blue-900'
      });
    }

    // Re-copy existing approval link (for pending campaigns with existing tokens)
    if (campaign.approval_status === 'pending' && campaign.approval_token) {
      actions.push({
        key: 'copy-approval-link',
        label: 'Copy Approval Link',
        icon: LinkIcon,
        onClick: () => handleCopyExistingApprovalLink(campaign.approval_token!),
        className: 'text-blue-700 hover:text-blue-900'
      });
    }

    // Publish to MailerLite (when approved but not yet published)
    if (campaign.approval_status === 'approved' && campaign.publish_status !== 'published' && campaign.publish_status !== 'sent') {
      actions.push({
        key: 'publish-mailerlite',
        label: 'Publish to MailerLite',
        icon: ChartBarIcon,
        onClick: () => handlePublishToMailerLite(campaign.id),
        className: 'text-green-700 hover:text-green-900'
      });
    }

    // Schedule (when approved - allow scheduling for approved campaigns)
    if (campaign.approval_status === 'approved') {
      actions.push({
        key: 'schedule',
        label: 'Schedule',
        icon: ClockIcon,
        onClick: () => setScheduleModalCampaign(campaign),
        className: 'text-blue-700 hover:text-blue-900'
      });
    }

    // Send Now (when published)
    if (campaign.publish_status === 'published') {
      actions.push({
        key: 'send-now',
        label: 'Send Now',
        icon: SparklesIcon,
        onClick: () => handleSendCampaign(campaign.id),
        className: 'text-purple-700 hover:text-purple-900'
      });
    }

    // Cancel schedule (when scheduled)
    if (campaign.publish_status === 'scheduled') {
      actions.push({
        key: 'cancel-schedule',
        label: 'Cancel Schedule',
        icon: XMarkIcon,
        onClick: () => handleCancelSchedule(campaign.id, campaign.name),
        className: 'text-orange-700 hover:text-orange-900'
      });
    }

    // Delete
    if (campaign.approval_status === 'draft' ||
        campaign.approval_status === 'rejected' ||
        (campaign.publish_status !== 'sent' && campaign.publish_status !== 'scheduled')) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        icon: TrashIcon,
        onClick: () => handleDeleteCampaign(campaign.id, campaign.name),
        className: 'text-red-700 hover:text-red-900'
      });
    }

    return actions;
  };

  const getActionButtons = (campaign: Campaign) => {
    const primaryAction = getPrimaryAction(campaign);
    const secondaryActions = getSecondaryActions(campaign);
    const isDropdownOpen = openDropdown === campaign.id;

    return (
      <div className="flex items-center justify-end space-x-2">
        {/* Primary Action */}
        {primaryAction}

        {/* Secondary Actions Dropdown */}
        {secondaryActions.length > 0 && (
          <div className="relative">
            <button
              data-dropdown-id={campaign.id}
              onClick={(e) => {
                if (isDropdownOpen) {
                  setOpenDropdown(null);
                } else {
                  const positionData = calculateDropdownPosition(e.currentTarget);
                  setDropdownPosition(prev => ({ ...prev, [campaign.id]: positionData }));
                  setOpenDropdown(campaign.id);
                }
              }}
              className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>

            {isDropdownOpen && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setOpenDropdown(null)}
                />

                {/* Dropdown menu with pixel-perfect CSS-in-JS positioning */}
                <div
                  ref={el => dropdownRefs.current[campaign.id] = el}
                  className="bg-white rounded-md shadow-lg border border-gray-200"
                  style={{
                    position: 'fixed',
                    top: dropdownPosition[campaign.id]?.coordinates.top,
                    bottom: dropdownPosition[campaign.id]?.coordinates.bottom,
                    right: dropdownPosition[campaign.id]?.coordinates.right || 0,
                    zIndex: 20,
                    width: '12rem', // w-48 equivalent
                    maxHeight: '240px',
                    overflowY: 'auto'
                  }}
                >
                  <div className="py-1">
                    {secondaryActions.map((action) => {
                      const IconComponent = action.icon;
                      return (
                        <button
                          key={action.key}
                          onClick={() => {
                            action.onClick();
                            setOpenDropdown(null);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center ${action.className}`}
                        >
                          <IconComponent className="h-4 w-4 mr-2" />
                          {action.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Campaigns</h2>
          <p className="text-gray-600">Create and manage AI-powered email campaigns</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={onGenerateContent}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
          >
            <BeakerIcon className="h-4 w-4 mr-2" />
            Generate with AI
          </button>
          <button
            onClick={onCreateCampaign}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Create Campaign
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-gray-200">
        <div className="overflow-x-auto">
          <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'campaigns'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <PlusIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">All </span>Campaigns
            </button>
            <button
              onClick={() => setActiveTab('scheduler')}
              className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'scheduler'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
              Schedule
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'history'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ChartBarIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
              History
            </button>
            <button
              onClick={() => setActiveTab('voice-settings')}
              className={`py-2 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
                activeTab === 'voice-settings'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <MicrophoneIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Voice </span>Settings
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'campaigns' && (
        <>
          {/* Filters */}
          <div className="flex space-x-4">
        <select
          value={filterApprovalStatus}
          onChange={(e) => setFilterApprovalStatus(e.target.value)}
          className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="all">All Statuses</option>
          <option value="draft">Drafts</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border-gray-300 py-2 pl-3 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        >
          <option value="all">All Campaigns</option>
          <option value="ready">Ready to Publish</option>
          <option value="published">Published</option>
          <option value="scheduled">Scheduled</option>
          <option value="sent">Sent</option>
        </select>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Campaigns Table */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="overflow-hidden">
          <table className="w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                Campaign
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 hidden sm:table-cell">
                Segment
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 hidden lg:table-cell">
                Data Quality
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/8">
                Status
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12 hidden md:table-cell">
                Recipients
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6 hidden xl:table-cell">
                Dates
              </th>
              <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {campaigns.map((campaign) => (
              <tr key={campaign.id} className="hover:bg-gray-50">
                <td className="px-3 sm:px-6 py-4">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate" title={campaign.name}>
                      {campaign.name}
                    </div>
                    <div className="text-sm text-gray-500 truncate" title={campaign.subject}>
                      {campaign.subject}
                    </div>
                    {/* Show additional info on mobile */}
                    <div className="sm:hidden mt-1 space-y-1">
                      <div className="text-xs text-gray-500">
                        {campaign.segment_name || 'No Segment'}
                        {campaign.segment_leads && ` • ${campaign.segment_leads} leads`}
                      </div>
                      <div className="text-xs text-gray-500 md:hidden">
                        {campaign.estimated_recipients || 0} recipients
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap hidden sm:table-cell">
                  <div className="text-sm text-gray-900">{campaign.segment_name || 'No Segment'}</div>
                  <div className="text-sm text-gray-500">
                    {campaign.segment_leads ? `${campaign.segment_leads} leads` : ''}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap hidden lg:table-cell">
                  {getDataQualityInfo(campaign)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(campaign.approval_status, campaign.publish_status)}
                    {/* Show feedback indicator when approval feedback exists */}
                    {campaign.approval_feedback &&
                     (campaign.approval_status === 'approved' || campaign.approval_status === 'rejected') && (
                      <span
                        className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700 cursor-help"
                        title="Has approval feedback - view in preview"
                      >
                        💬
                      </span>
                    )}
                  </div>
                  {/* Show dates on mobile */}
                  <div className="xl:hidden mt-1">
                    <div className="text-xs text-gray-500">
                      {formatDate(campaign.created_at)}
                    </div>
                    {campaign.publish_status === 'scheduled' && campaign.scheduled_for && (
                      <div className="text-xs text-purple-600 font-medium">
                        📅 {formatDate(campaign.scheduled_for)}
                      </div>
                    )}
                    {campaign.publish_status === 'sent' && campaign.sent_at && (
                      <div className="text-xs text-green-600 font-medium">
                        ✅ {formatDate(campaign.sent_at)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900 hidden md:table-cell">
                  {campaign.estimated_recipients || 0}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500 hidden xl:table-cell">
                  <div>
                    <div>Created: {formatDate(campaign.created_at)}</div>
                    {campaign.publish_status === 'scheduled' && campaign.scheduled_for && (
                      <div className="text-purple-600 font-medium mt-1">
                        📅 Scheduled: {formatDate(campaign.scheduled_for)}
                      </div>
                    )}
                    {campaign.publish_status === 'sent' && campaign.sent_at && (
                      <div className="text-green-600 font-medium mt-1">
                        ✅ Sent: {formatDate(campaign.sent_at)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {getActionButtons(campaign)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {campaigns.length === 0 && (
          <div className="text-center py-12">
            <BeakerIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first AI-powered campaign.
            </p>
            <div className="mt-6 space-x-3">
              <button
                onClick={onGenerateContent}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
              >
                <BeakerIcon className="h-4 w-4 mr-2" />
                Generate with AI
              </button>
              <button
                onClick={onCreateCampaign}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Create Manually
              </button>
            </div>
          </div>
        )}
      </div>

        </>
      )}

      {activeTab === 'scheduler' && (
        <CampaignScheduler 
          onRescheduleCampaign={(campaign) => setScheduleModalCampaign(campaign)}
        />
      )}

      {activeTab === 'history' && (
        <CampaignHistory />
      )}

      {activeTab === 'voice-settings' && (
        <div className="mt-6">
          <VoiceSettingsManager />
        </div>
      )}

      {/* Schedule Modal */}
      {scheduleModalCampaign && (
        <ScheduleModal
          campaign={scheduleModalCampaign}
          isOpen={true}
          onClose={() => setScheduleModalCampaign(null)}
          onScheduled={() => {
            setScheduleModalCampaign(null);
            loadCampaigns();
          }}
        />
      )}


      {/* AI Editor Modal */}
      {aiEditorCampaign && (
        <CampaignAIEditor
          campaign={aiEditorCampaign}
          onSave={(updatedCampaign) => {
            // Refresh campaigns after AI edit
            loadCampaigns();
            setAiEditorCampaign(null);
          }}
          onClose={() => setAiEditorCampaign(null)}
        />
      )}

      {/* Campaign Preview Modal */}
      {previewCampaign && (
        <CampaignPreview
          campaign={previewCampaign}
          onClose={() => setPreviewCampaign(null)}
          onSave={(updatedCampaign) => {
            // Update the local state with the updated campaign
            setCampaigns(prev => prev.map(c =>
              c.id === updatedCampaign.id ? updatedCampaign : c
            ));

            // Update the preview campaign state so the modal shows latest content
            setPreviewCampaign(updatedCampaign);

            console.log('✅ Campaign updated successfully in dashboard');
          }}
        />
      )}

    </div>
  );
};

export default CampaignDashboard;